import {
  createOAuthClient,
  findExistingRoles,
  findExistingOAuthClients,
  deleteRole,
  deleteOAuthClient,
} from './genesys-api';
import {
  registerOrganization,
  checkOrganization,
  unregisterOrganization,
  updateOrgCredentials,
  type RegisterOrgPayload,
} from './wizard-backend';

/**
 * Extract a human-readable message from any thrown value,
 * including Genesys Platform SDK error objects.
 */
function extractErrorMessage(e: unknown): string {
  if (e instanceof Error) return e.message;
  if (typeof e === 'object' && e !== null) {
    const obj = e as Record<string, unknown>;
    if (typeof obj.message === 'string') return obj.message;
    if (typeof obj.body === 'string') return obj.body;
    if (typeof obj.body === 'object' && obj.body !== null) {
      const body = obj.body as Record<string, unknown>;
      if (typeof body.message === 'string') return body.message;
    }
    try { return JSON.stringify(e); } catch { /* fallthrough */ }
  }
  return String(e);
}

interface InstalledData {
  oauthClient?: { id: string; name: string; secret: string };
}

export type InstallMode = 'create' | 'update';

export type ProvisioningStep = 'oauth-client' | 'register-org' | 'update-credentials';

export interface ProvisioningProgress {
  step: ProvisioningStep;
  status: 'pending' | 'in-progress' | 'completed' | 'failed';
  error?: string;
}

let installedData: InstalledData = {};

/** An OAuth client a failed install created and could not remove. Id and name only. */
export interface LeftoverOAuthClient {
  id: string;
  name: string;
  genesysOrgId: string;
}

const LEFTOVER_CLIENT_KEY = 'mediabriz_wizard_leftover_oauth_client';

function rememberLeftoverClient(client: LeftoverOAuthClient): void {
  try {
    sessionStorage.setItem(LEFTOVER_CLIENT_KEY, JSON.stringify(client));
  } catch {
    // Storage blocked: the error screen still names the client.
  }
}

export function forgetLeftoverClient(): void {
  try {
    sessionStorage.removeItem(LEFTOVER_CLIENT_KEY);
  } catch {
    // Nothing stored.
  }
}

/** The client this tab could not roll back for this org, if any. */
export function readLeftoverClient(genesysOrgId: string): LeftoverOAuthClient | null {
  try {
    const raw = sessionStorage.getItem(LEFTOVER_CLIENT_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw) as Partial<LeftoverOAuthClient>;
    if (typeof parsed.id !== 'string' || !parsed.id || typeof parsed.name !== 'string') return null;
    if (parsed.genesysOrgId !== genesysOrgId) return null;
    return { id: parsed.id, name: parsed.name, genesysOrgId };
  } catch {
    return null;
  }
}

/** Retries the rollback after a new sign-in. True when the client is gone. */
export async function removeLeftoverClient(client: LeftoverOAuthClient): Promise<boolean> {
  try {
    await deleteOAuthClient(client.id);
    forgetLeftoverClient();
    console.info('[WIZARD] Removed the OAuth client a failed install left behind', { clientId: client.id });
    return true;
  } catch (e) {
    // Already gone counts as removed.
    if ((e as { status?: number } | null)?.status === 404) {
      forgetLeftoverClient();
      return true;
    }
    console.warn('Failed to remove leftover OAuth client:', extractErrorMessage(e));
    return false;
  }
}

/**
 * Deletes the client the install just created. When that fails (for example
 * the session expired), remembers it so the next sign-in in this tab retries,
 * and the error screen names it.
 */
async function rollbackOAuthClient(clientId: string, genesysOrgId: string): Promise<void> {
  try {
    await deleteOAuthClient(clientId);
  } catch (e) {
    console.warn('Failed to rollback OAuth client:', extractErrorMessage(e));
    rememberLeftoverClient({ id: clientId, name: installedData.oauthClient?.name ?? '', genesysOrgId });
  }
}

export interface ExistingOrgStatus {
  exists: boolean;
  hasCredentials: boolean;
  organization?: string;
  region?: string;
  /** True only when Mediabriz Guard is enabled. Shows the Guard block in Settings. */
  scanEnabled?: boolean;
  /** Operator flag. Absent means Login Limit is off. Shows the Login Limit section. */
  loginLimitEnabled?: boolean;
  /** Conversation Actions flag; missing on the org record = enabled (Settings gate). */
  aiActionsEnabled?: boolean;
  /** Operator flag. Missing = on; false = Login Limit only (no conversation pages). */
  conversationsEnabled?: boolean;
}

/**
 * Check if MediaBriz is already installed in this org by querying
 * the MediaBriz backend organization table using the Genesys Cloud org ID.
 * Returns detailed status including whether credentials are configured.
 * Throws on network/service errors so the caller can show a user-friendly message.
 */
export async function checkExisting(
  genesysOrgId: string,
  genesysRegion: string,
): Promise<ExistingOrgStatus> {
  const result = await checkOrganization(genesysOrgId, genesysRegion);
  return {
    exists: result.exists,
    hasCredentials: result.hasCredentials,
    organization: result.organization,
    region: result.region,
    scanEnabled: result.scanEnabled === true,
    loginLimitEnabled: result.loginLimitEnabled === true,
    aiActionsEnabled: result.aiActionsEnabled !== false,
    conversationsEnabled: result.conversationsEnabled !== false,
  };
}

/**
 * Run the full installation: create OAuth client, register org (or update credentials).
 * mode 'create': new org -- creates OAuth client + registers org via POST.
 * mode 'update': pre-provisioned org -- creates OAuth client + updates credentials via PUT.
 * Calls onProgress for each step so the UI can update the progress indicator.
 */
export async function install(
  registrationPayload: Omit<RegisterOrgPayload, 'client_id' | 'client_secret'>,
  onProgress: (progress: ProvisioningProgress) => void,
  mode: InstallMode = 'create',
  existingOrg?: { organization: string; region: string },
): Promise<{ status: boolean; cause?: string }> {
  installedData = {};

  // Step 1: Create OAuth Client
  onProgress({ step: 'oauth-client', status: 'in-progress' });
  try {
    const oauthClient = await createOAuthClient();
    installedData.oauthClient = oauthClient;
    onProgress({ step: 'oauth-client', status: 'completed' });
  } catch (e) {
    const msg = extractErrorMessage(e);
    onProgress({ step: 'oauth-client', status: 'failed', error: msg });
    throw e;
  }

  if (mode === 'update' && existingOrg) {
    // Step 2 (update mode): Update credentials on existing pre-provisioned org
    onProgress({ step: 'update-credentials', status: 'in-progress' });
    try {
      const result = await updateOrgCredentials(
        {
          organization: existingOrg.organization,
          region: existingOrg.region,
          client_id: installedData.oauthClient!.id,
          client_secret: installedData.oauthClient!.secret,
          dataRegion: registrationPayload.dataRegion,
        },
        registrationPayload.genesysOrgId,
        registrationPayload.region,
      );

      if (result.status) {
        onProgress({ step: 'update-credentials', status: 'completed' });
      } else {
        await rollbackOAuthClient(installedData.oauthClient!.id, registrationPayload.genesysOrgId);
        onProgress({ step: 'update-credentials', status: 'failed', error: result.cause });
      }

      return result;
    } catch (e) {
      await rollbackOAuthClient(installedData.oauthClient!.id, registrationPayload.genesysOrgId);
      const msg = extractErrorMessage(e);
      onProgress({ step: 'update-credentials', status: 'failed', error: msg });
      throw e;
    }
  }

  // Step 2 (create mode): Register with MediaBriz backend
  onProgress({ step: 'register-org', status: 'in-progress' });
  try {
    const result = await registerOrganization({
      ...registrationPayload,
      client_id: installedData.oauthClient!.id,
      client_secret: installedData.oauthClient!.secret,
    });

    if (result.status) {
      onProgress({ step: 'register-org', status: 'completed' });
    } else {
      await rollbackOAuthClient(installedData.oauthClient!.id, registrationPayload.genesysOrgId);
      onProgress({ step: 'register-org', status: 'failed', error: result.cause });
    }

    return result;
  } catch (e) {
    await rollbackOAuthClient(installedData.oauthClient!.id, registrationPayload.genesysOrgId);
    const msg = extractErrorMessage(e);
    onProgress({ step: 'register-org', status: 'failed', error: msg });
    throw e;
  }
}

/**
 * Uninstall: remove Genesys Cloud objects and deregister from MediaBriz backend.
 * Continues through all cleanup steps even if individual items fail.
 */
export async function uninstall(
  genesysOrgId: string,
  gcEnvironment: string,
): Promise<void> {
  const errors: string[] = [];

  // Remove the legacy "Mediabriz App" role (exact name; see findExistingRoles)
  try {
    const roles = await findExistingRoles();
    for (const role of roles) {
      try {
        await deleteRole(role.id);
      } catch (e) {
        const msg = extractErrorMessage(e);
        console.warn(`Failed to delete role ${role.name} (${role.id}):`, msg);
        errors.push(`Role ${role.name}: ${msg}`);
      }
    }
  } catch (e) {
    const msg = extractErrorMessage(e);
    console.warn('Failed to find existing roles:', msg);
    errors.push(`Find roles: ${msg}`);
  }

  // Remove existing OAuth clients (CODE grant type only).
  // The Login Limit client is CLIENT-CREDENTIALS and is removed by the wizard
  // backend during unregister, only when the feature was connected. Do not
  // widen this filter.
  try {
    const clients = await findExistingOAuthClients();
    for (const client of clients) {
      try {
        await deleteOAuthClient(client.id);
      } catch (e) {
        const msg = extractErrorMessage(e);
        console.warn(`Failed to delete OAuth client ${client.name} (${client.id}):`, msg);
        errors.push(`OAuth client ${client.name}: ${msg}`);
      }
    }
  } catch (e) {
    const msg = extractErrorMessage(e);
    console.warn('Failed to find existing OAuth clients:', msg);
    errors.push(`Find OAuth clients: ${msg}`);
  }

  // Deregister from MediaBriz backend
  try {
    await unregisterOrganization(genesysOrgId, gcEnvironment);
  } catch (e) {
    const msg = extractErrorMessage(e);
    console.warn('Failed to deregister from backend:', msg);
    errors.push(`Backend deregistration: ${msg}`);
  }

  if (errors.length > 0) {
    throw new Error(`Uninstall completed with errors:\n${errors.join('\n')}`);
  }
}
